import { orderMapper } from "~/assets/js/utils";

export default {
  async fetch({ dispatch, commit }, params) {
    commit("SET_LOADING", true);
    try {
      const { agency } = params;
      commit("SET_AGENCY", agency);
      await dispatch("setLastVersion");
      await dispatch("setComputers");
      await dispatch("updateTags");
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async setComputers({ commit, state, rootGetters }, { filter, defaultFilter } = {}) {
    const { page, sortBy, orderBy, search, agency } = state;
    const size = rootGetters["application/getPageSize"];
    try {
      const computers = await this.$services.computer.fetch(agency, {
        size,
        page,
        sortBy,
        orderBy,
        filter: filter ?? search,
        defaultFilter,
      });
      commit("SET_COMPUTERS", computers);
    } catch (error) {
      throw new Error(error);
    }
  },

  async setLastVersion({ commit }) {
    try {
      const { version } = await this.$services.computer.lastVersion();
      commit("SET_LAST_VERSION", version);
    } catch (error) {
      throw new Error(error);
    }
  },

  async updateTags({ commit, state }) {
    try {
      const tags = await this.$services.computer.tags(state.agency);
      commit("SET_TAGS", tags);
    } catch (error) {
      throw new Error(error);
    }
  },

  async setCurrentComputer({ commit }, id) {
    try {
      const currentComputer = await this.$services.computer.fetchOne(id);
      commit("SET_CURRENT_COMPUTER", currentComputer);
    } catch (error) {
      if (error.response?.data?.message === "agent.notfound") {
        this.$feedback.error("feedback.api.error.agent.notfound");
      } else {
        throw new Error(error);
      }
    }
  },

  async resetCurrentComputer({ commit }) {
    commit("RESET_CURRENT_COMPUTER");
  },

  async setComputer({ commit, dispatch }, { savedAgent, id }) {
    try {
      const updatedAgent = await this.$services.computer.patch(id, { tags: savedAgent.tags });
      commit("SET_COMPUTER", updatedAgent);
      await dispatch("updateTags");
      commit("ADD_TAGS", savedAgent.tags);
      this.$feedback.ok("feedback.update.computer.ok");
    } catch {
      this.$feedback.error("feedback.update.computer.error");
    }
  },

  async deleteComputer({ commit }, computerId) {
    try {
      await this.$services.computer.remove(computerId);
      commit("DELETE_COMPUTER", computerId);
    } catch (error) {
      throw new Error(error);
    }
  },

  async uninstallAgent({ commit }, { agentId }) {
    try {
      await this.$services.computer.uninstall(agentId);
      commit("MARK_FOR_REMOVAL", agentId);
    } catch (error) {
      throw new Error(error);
    }
  },

  async installAgent(_, { agentId }) {
    try {
      await this.$services.computer.update(agentId);
    } catch (error) {
      throw new Error(error);
    }
  },

  async moveSelectedComputers({ commit, rootGetters }, { destination, selection, chunk }) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    try {
      await this.$services.computer.moveSelected(agencyId, {
        agencyId: destination,
        agentIds: selection,
        selectAll: chunk ?? false,
      });
      commit("REMOVE_SELECTED_COMPUTERS", selection);
      this.$feedback.ok("computers.globalActions.move.ok");
    } catch {
      this.$feedback.error("computers.globalActions.move.error");
    }
  },

  async moveAllComputers({ dispatch, rootGetters }, { destination }) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    try {
      await this.$services.computer.moveAll(agencyId, { agencyId: destination });
      await dispatch("setComputers");
      this.$feedback.ok("computers.globalActions.move.ok");
    } catch {
      this.$feedback.error("computers.globalActions.move.error");
    }
  },

  async deleteSelectedComputers({ commit, rootGetters }, { selection, chunk }) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    try {
      await this.$services.computer.deleteSelected(agencyId, {
        agentIds: selection,
        selectAll: chunk ?? false,
      });
      commit("REMOVE_SELECTED_COMPUTERS", selection);
      this.$feedback.ok("feedback.delete.computers.bulk.ok");
    } catch {
      this.$feedback.error("feedback.delete.computers.bulk.error");
    }
  },

  async uninstallSelectedComputers({ commit, rootGetters }, { selection, chunk }) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    try {
      await this.$services.computer.uninstallSelected(agencyId, {
        agentIds: selection,
        selectAll: chunk ?? false,
      });
      commit("UNINSTALL_SELECTED_COMPUTERS", selection);
      this.$feedback.ok("feedback.uninstall.computers.bulk.ok");
    } catch {
      this.$feedback.error("feedback.uninstall.computers.bulk.error");
    }
  },

  async tagSelectedComputers({ dispatch, rootGetters }, { agentIds, tags, chunk, action }) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    try {
      await this.$services.computer.tagSelected(agencyId, action, {
        agentIds,
        tags,
        selectAll: chunk ?? false,
      });
      await dispatch("updateTags");
      this.$feedback.ok("computers.globalActions.tagSelectedAgents.ok");
    } catch {
      this.$feedback.error("computers.globalActions.tagSelectedAgents.error");
    }
  },

  async uninstallAllAgents({ commit, rootGetters, state }) {
    try {
      const pageSize = rootGetters["application/getPageSize"];
      const updatedComputers = await this.$services.computer.uninstallAll(state.agency, {
        pageSize,
      });
      commit("SET_COMPUTERS", updatedComputers);
    } catch (error) {
      throw new Error(error.message);
    }
  },

  async deleteAllComputers({ commit, state }) {
    try {
      await this.$services.computer.deleteAll(state.agency);
      commit("DELETE_ALL_COMPUTERS");
    } catch (error) {
      throw new Error(error.message);
    }
  },

  async vaccinateSelectedComputers({ commit, rootGetters }, { vaccineId, selection, chunk }) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    try {
      const updatedAgents = await this.$services.computer.vaccinateSelected(vaccineId, agencyId, {
        agentIds: selection,
        selectAll: chunk ?? false,
      });
      commit("SET_VACCINATED_STATUS", updatedAgents);
      commit("SET_DEPLOYMENT_VACCINATED_STATUS", { agentIds: selection, vaccineId });
      this.$feedback.ok("computers.globalActions.vaccinateSelectedAgents.ok");
    } catch {
      this.$feedback.error("computers.globalActions.vaccinateSelectedAgents.error");
    }
  },

  async executeVaccine({ commit }, { vaccineId, agentId }) {
    const vaccineIds = [vaccineId];
    try {
      await this.$services.computer.executeVaccine(agentId, vaccineId, vaccineIds);
      commit("SET_DEPLOYMENT_STATUS", { agentId, vaccineIds });
      this.$feedback.ok("feedback.execute.vaccine.ok");
    } catch {
      this.$feedback.error("feedback.execute.vaccine.error");
    }
  },

  async stopVaccine({ commit }, { vaccineId, agentId }) {
    const vaccineIds = [vaccineId];
    try {
      await this.$services.computer.stopVaccine(agentId, vaccineId, vaccineIds);
      commit("SET_DEPLOYMENT_STATUS", { agentId, vaccineIds });
      this.$feedback.ok("feedback.stop.vaccine.ok");
    } catch {
      this.$feedback.error("feedback.stop.vaccine.error");
    }
  },

  async fetchVaccineDeployments({ commit }, { agentId, requestParams }) {
    const { page, sortBy, orderBy, size = 10 } = requestParams;
    try {
      const vaccineDeployments = await this.$services.computer.fetchDeployments(agentId, {
        size,
        page,
        sortBy,
        orderBy: orderMapper(orderBy),
      });
      vaccineDeployments.sortBy = sortBy;
      vaccineDeployments.orderBy = orderBy;
      commit("SET_VACCINE_DEPLOYMENTS", { parent: agentId, vaccineDeployments });
    } catch (error) {
      throw new Error(error);
    }
  },

  removeVaccineDeployments({ commit }, agentId) {
    commit("REMOVE_VACCINE_DEPLOYMENTS", { parent: agentId });
  },

  removeAllVaccineDeployments({ commit }) {
    commit("REMOVE_ALL_VACCINE_DEPLOYMENTS");
  },

  async prepareCsv({ dispatch, commit, state }, params) {
    try {
      await this.$services.computer.preparecsv(state.agency, params);
      await dispatch("application/setDownloads", {}, { root: true });
      commit("application/SET_IS_WAITING_FOR_DOWNLOADS", true, { root: true });
      this.$feedback.ok("generic.download.ok");
    } catch {
      this.$feedback.error("generic.download.error");
    }
  },
};
